import { Flex, Icon, Text } from '@lideralia/alife-uikit';
import { IconClock, IconSmile } from '@lideralia/alife-uikit/dist/atoms/Icons';
import {
  AdminAgency,
  DigitalDeletion,
  ElderlyCare,
  FlowerHome,
  GriefSupport,
  Management,
  PendantJewelry,
  Tree,
} from '@lideralia/alife-uikit/dist/atoms/imgs';
import { EditorBanner } from '@lideralia/alife-uikit/dist/organisms/BiographicalEditor/EditorBanner';
import { CardService } from '@lideralia/alife-uikit/dist/organisms/Cards';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SERVICE_CATEGORIES } from '../../../../utilities';
import { KEYS_ANALYTICS, setEventAnalytics } from '../Analytics';

// Mapeo de slugs de servicios a eventos de analytics
const SERVICE_CATEGORY_ANALYTICS_MAP = {
  ecommerce: KEYS_ANALYTICS.VIEW_ECOMMERCE_BUTTON_CLICKED,
  udianas: KEYS_ANALYTICS.VIEW_UDIANAS_BUTTON_CLICKED,
  arrangements: KEYS_ANALYTICS.VIEW_ARRANGEMENTS_BUTTON_CLICKED,
};

// Mapeo de slugs de servicios a iconos
const SERVICE_ICON_MAP = {
  ecommerce: FlowerHome,
  udianas: Tree,
  arrangements: Management,
  'bereavement-support': GriefSupport,
  'dependency-assistance': ElderlyCare,
  'digital-deletion': DigitalDeletion,
  'administrative-agency': AdminAgency,
  'jewelry-store': PendantJewelry,
};

const ICON_MAP = {
  IconSmile,
  IconClock,
};

const ServicesHeader = ({ title }) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    marginTop={1}
    marginBottom={4}
  >
    <Text
      color="black84"
      sizeText="display24"
      fontWeight="bold"
      paddingBottom={0}
      textAlign="center"
    >
      {title}
    </Text>
    <Text color="black60" sizeText="display14" paddingBottom={0} textAlign="center">
      <FormattedMessage id="services.noServicesDescription" />
    </Text>
  </Flex>
);

export const ServicesComponent = ({
  showServicesOfCategory,
  showEmptyCategoriesMessage = false,
  lineServices = [],
  isAdmin = false,
  onEditorBannerClick = null,
  ...props
}) => {
  const user = useSelector((state) => state.user);
  const line = useSelector((state) => state.line);
  const history = useHistory();
  const intl = useIntl();

  const getAnalyticsEvent = (service) =>
    SERVICE_CATEGORY_ANALYTICS_MAP[service.slug] || `view_service_${service.slug}_button_clicked`;

  const getServiceIcon = (service) => {
    // Primero intentar por slug específico, luego por categoría, luego icono por defecto
    const IconComponent = SERVICE_ICON_MAP[service.slug] || GriefSupport;
    return <IconComponent width="48px" height="48px" />;
  };

  const getIcon = (icon) => {
    const IconComponent = ICON_MAP[icon] || IconSmile;
    return <IconComponent />;
  };

  const getRelativePath = (fullUrl) => {
    try {
      const url = new URL(fullUrl);
      return url.pathname; // Retorna solo la parte de la ruta
    } catch (error) {
      // Si no es una URL válida, retornar la URL original
      return fullUrl;
    }
  };

  const goToService = (service) => {
    if (service.url) {
      let analyticsEvent;
      if (service.external) {
        analyticsEvent = 'outbound_link_clicked_flowers';
        window.open(service.url, '_blank');
      } else {
        analyticsEvent = getAnalyticsEvent(service);
        const relativePath = getRelativePath(service.url);
        history.push(relativePath);
      }
      setEventAnalytics(analyticsEvent, user, window?.location?.pathname, line?.active);
    }
  };

  const showEditorBanner =
    isAdmin &&
    onEditorBannerClick &&
    (!showServicesOfCategory || showServicesOfCategory === SERVICE_CATEGORIES.TRIBUTE);

  return (
    <Flex
      width="100%"
      maxWidth="339px"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      margin="0"
      gap={2}
      style={{ boxSizing: 'border-box' }}
      {...props}
    >
      {showEditorBanner && (
        <EditorBanner
          onClick={onEditorBannerClick}
          badgeLabel={intl.formatMessage({
            id: line?.active?.biographicalMemoryMyUuid
              ? 'biographicalEditor.banner.ctaView'
              : 'biographicalEditor.banner.ctaTry',
          })}
          title={intl.formatMessage({ id: 'biographicalEditor.title' })}
        />
      )}
      {lineServices &&
        lineServices.map((service) => (
          <CardService
            key={service.slug}
            id={service.slug}
            icon={getServiceIcon(service)}
            titleLabel={service.name ?? ''}
            actionButton={() => goToService(service)}
            colorButton={service.color || 'orange'}
          >
            {service.info && (
              <Flex
                paddingLeft={3}
                paddingRight={3}
                marginBottom={3}
                width="100%"
                justifyContent="center"
              >
                <Flex
                  flexDirection="row"
                  alignItems="center"
                  backgroundColor="#fdf5e6"
                  border="1px solid orange"
                  borderRadius="100px"
                  width="fit-content"
                  paddingX="6px"
                  paddingY="2px"
                >
                  <Icon color="grey" sizeIcon="display15">
                    {getIcon(service.info.icon)}
                  </Icon>
                  <Text color="black84" sizeText="display12" fontWeight="bold" paddingLeft={2}>
                    {service.info.text}
                  </Text>
                </Flex>
              </Flex>
            )}

            {service.footer && (
              <Flex flexDirection="column" backgroundColor="#f5f8ea">
                <Flex
                  width="100%"
                  padding={2}
                  borderTopColor="black12"
                  borderTopStyle="solid"
                  borderTopWidth="0px"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon color="#8A9C49" sizeIcon="display20">
                    {getIcon(service.footer.icon)}
                  </Icon>
                  <Text color="#8A9C49" sizeText="display13" fontWeight="bold" paddingLeft={1}>
                    {service.footer.text}
                  </Text>
                </Flex>
              </Flex>
            )}
          </CardService>
        ))}

      {!showEditorBanner &&
        showEmptyCategoriesMessage &&
        showServicesOfCategory === SERVICE_CATEGORIES.TRIBUTE &&
        (!lineServices || lineServices.length === 0) && (
          <>
            <ServicesHeader title={<FormattedMessage id="services.tributeTitle" />} />
            <img
              src={`${process.env.PUBLIC_URL}/images/illustration_service.png`}
              alt=""
              width="339px"
            />
          </>
        )}
      {!showEditorBanner &&
        showEmptyCategoriesMessage &&
        showServicesOfCategory === SERVICE_CATEGORIES.SERVICE &&
        (!lineServices || lineServices.length === 0) && (
          <>
            <ServicesHeader title={<FormattedMessage id="services.serviceTitle" />} />
            <img
              src={`${process.env.PUBLIC_URL}/images/illustration_tribute.png`}
              alt=""
              width="339px"
            />
          </>
        )}
      {!showEditorBanner &&
        showEmptyCategoriesMessage &&
        !showServicesOfCategory &&
        (!lineServices || lineServices.length === 0) && (
          <>
            <ServicesHeader title={<FormattedMessage id="services.allTitle" />} />
            <img
              src={`${process.env.PUBLIC_URL}/images/illustration_all.png`}
              alt=""
              width="339px"
            />
          </>
        )}
    </Flex>
  );
};
